import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Check, AlertTriangle, Mail, ChevronDown, ChevronRight,
  ExternalLink, User, Send,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inp = 'w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/25 outline-none focus:border-[#C58C40]/50';
const lbl = 'text-white/50 text-xs mb-1 block';
const VMAP = { V1: 'v1_relevo', V2: 'v2_internacionalizacion', V3: 'v3_financiera', V4: 'v4_crecimiento' };
const VLABEL = { V1: 'V1 Relevo', V2: 'V2 Internac.', V3: 'V3 Financiera', V4: 'V4 Crecimiento' };
const ROLE_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

// Decisión → correo asociado
const DECISION_OPTIONS = [
  { key: 'Admitido',   label: 'Admitido',    email: 'A2', emailLabel: 'A2 · Perfil activado',
    active: 'bg-emerald-400/15 border-emerald-400/40 text-emerald-300',
    idle:   'border-white/10 text-white/60 hover:border-emerald-400/30 hover:text-emerald-300' },
  { key: 'En reserva', label: 'En reserva',  email: 'A4', emailLabel: 'A4 · En reserva',
    active: 'bg-amber-400/15 border-amber-400/40 text-amber-300',
    idle:   'border-white/10 text-white/60 hover:border-amber-400/30 hover:text-amber-300' },
  { key: 'Rechazado',  label: 'No admitido', email: 'A3', emailLabel: 'A3 · No admitido',
    active: 'bg-red-400/15 border-red-400/40 text-red-300',
    idle:   'border-white/10 text-white/60 hover:border-red-400/30 hover:text-red-300' },
];

// Etiquetas legibles de las claves más frecuentes del cuestionario del advisor.
// Si aparece una clave no listada se muestra tal cual (formateada).
const FIELD_LABELS = {
  nombre: 'Nombre',
  email: 'Email',
  telefono: 'Teléfono',
  linkedin: 'LinkedIn',
  cargo: 'Cargo actual',
  empresaActual: 'Empresa actual',
  experiencia: 'Experiencia',
  areasEspecializacion: 'Áreas de especialización',
  sectoresExperiencia: 'Sectores',
  nivel: 'Nivel',
  disponibilidad: 'Disponibilidad',
  modalidad: 'Modalidad',
  zonaPresencial: 'Zona presencial',
  idiomas: 'Idiomas',
  motivacion: 'Motivación',
  bio: 'Bio / experiencia relevante',
  aceptaModelo: 'Acepta el modelo',
  aceptaNDA: 'Acepta NDA',
  aceptaPrivacidad: 'Acepta privacidad',
  submittedAt: 'Enviado el',
};

const PRIORITY_KEYS = [
  'nombre', 'email', 'telefono', 'linkedin', 'cargo', 'empresaActual',
  'experiencia', 'areasEspecializacion', 'sectoresExperiencia',
  'nivel', 'disponibilidad', 'modalidad', 'zonaPresencial', 'idiomas',
  'motivacion', 'bio',
];

function formatVal(v) {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.length === 0 ? null : v.join(', ');
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
}

function humanizeKey(k) {
  return k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function CuestionarioBlock({ payload, linkedin }) {
  const [open, setOpen] = useState(true);
  const keys = Object.keys(payload || {});
  const hasData = keys.length > 0;

  if (!hasData) {
    return (
      <div className="border border-white/10 rounded p-4 mb-4 text-white/40 text-sm flex items-center gap-2" data-testid="ficha-no-cuestionario">
        <User size={14} className="text-white/30" />
        Candidato añadido a mano — no hay cuestionario asociado.
      </div>
    );
  }

  const ordered = [
    ...PRIORITY_KEYS.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !PRIORITY_KEYS.includes(k) && !['aceptaModelo', 'aceptaNDA', 'aceptaPrivacidad', 'submittedAt', 'id'].includes(k)),
  ];

  return (
    <div className="border border-white/10 rounded mb-4" data-testid="ficha-cuestionario">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-white text-sm font-medium flex items-center gap-2">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          Datos del cuestionario
        </span>
        <span className="text-white/30 text-[11px]">solo lectura</span>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-white/5 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
            {ordered.map((k) => {
              const val = formatVal(payload[k]);
              if (val === null) return null;
              const isLink = k === 'linkedin' && val;
              return (
                <div key={k}>
                  <p className="text-white/30 text-[10px] uppercase tracking-wider mb-1">
                    {FIELD_LABELS[k] || humanizeKey(k)}
                  </p>
                  {isLink ? (
                    <a href={val.startsWith('http') ? val : `https://${val}`} target="_blank" rel="noreferrer"
                      className="text-[#C58C40] text-sm break-words hover:underline inline-flex items-center gap-1">
                      {val}<ExternalLink size={11} />
                    </a>
                  ) : (
                    <p className="text-white/80 text-sm break-words whitespace-pre-wrap">{val}</p>
                  )}
                </div>
              );
            })}
          </div>
          {linkedin && !payload.linkedin && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <a href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`} target="_blank" rel="noreferrer"
                className="text-[#C58C40] text-sm inline-flex items-center gap-1 hover:underline">
                LinkedIn del candidato<ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Ficha({ token, config, candidateId, onBack }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [sending, setSending] = useState(null); // 'A2' | 'A3' | 'A4'
  const [toast, setToast] = useState(null); // { type: 'ok'|'err', text }

  const load = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/candidates/${candidateId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setC(await res.json());
  }, [token, candidateId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const saveDecision = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/candidates/${candidateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Admin-User': c.evaluador || 'admin',
        },
        body: JSON.stringify({
          resultado_final: c.resultado_final || null,
          notas_internas: c.notas_internas || '',
          evaluador: c.evaluador || null,
          nivel_manual: c.nivel_manual || null,
          v1_relevo: c.v1_relevo || 'No',
          v2_internacionalizacion: c.v2_internacionalizacion || 'No',
          v3_financiera: c.v3_financiera || 'No',
          v4_crecimiento: c.v4_crecimiento || 'No',
          r1: c.r1 || 'No',
          r2: c.r2 || 'No',
          r3: c.r3 || 'No',
          r4: c.r4 || 'No',
          r5: c.r5 || 'No',
          r6: c.r6 || 'No',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setC(data);
        setSaveMsg({ type: 'ok', text: 'Guardado' });
      } else {
        setSaveMsg({ type: 'err', text: data.detail || 'Error al guardar' });
      }
    } catch {
      setSaveMsg({ type: 'err', text: 'Error de conexión' });
    }
    setSaving(false);
  };

  const sendEmail = async (opt) => {
    if (!c.email) {
      showToast('err', 'Este candidato no tiene email para recibir el correo.');
      return;
    }
    const already = (c.emails_sent || {})[opt.email];
    if (already) {
      showToast('err', `El correo ${opt.email} ya se envió el ${already.slice(0, 16).replace('T', ' ')}.`);
      return;
    }
    // Guardarraíl mínimo: confirmar la decisión coincide con el correo elegido.
    if (c.resultado_final && c.resultado_final !== opt.key) {
      const ok = window.confirm(
        `La decisión guardada es "${c.resultado_final}" pero vas a enviar "${opt.emailLabel}".\n\n¿Continuar?`
      );
      if (!ok) return;
    }
    const ok = window.confirm(`¿Enviar "${opt.emailLabel}" a ${c.email}?`);
    if (!ok) return;
    setSending(opt.email);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/email/send-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template: opt.email, entity_type: 'advisor', entity_id: c.id }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('ok', `Correo ${opt.email} enviado a ${c.email}`);
        await load();
      } else {
        showToast('err', data.detail || 'El envío falló');
      }
    } catch {
      showToast('err', 'Error de conexión');
    }
    setSending(null);
  };

  if (!c) return <div className="text-white/30 text-sm py-16 text-center">Cargando ficha…</div>;

  const emailsSent = c.emails_sent || {};

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white text-sm" data-testid="ficha-back">
          <ArrowLeft size={14} />Volver a la bandeja
        </button>
        <div className="flex items-center gap-3">
          <span className="text-white/40 text-xs font-mono">{c.id}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${
            c.resultado_final === 'Admitido' ? 'bg-emerald-400/10 text-emerald-300'
            : c.resultado_final === 'En reserva' ? 'bg-amber-400/10 text-amber-300'
            : c.resultado_final === 'Rechazado' ? 'bg-red-400/10 text-red-300'
            : 'bg-white/5 text-white/40'
          }`}>
            {c.resultado_final || 'Sin decidir'}
          </span>
        </div>
      </div>

      {/* Nombre / contacto */}
      <div className="mb-6">
        <h2 className="text-white text-2xl font-light mb-1">{c.nombre || '—'}</h2>
        <p className="text-white/40 text-sm">
          {c.email || '—'}{c.telefono ? ` · ${c.telefono}` : ''}
        </p>
      </div>

      {/* Cuestionario (solo lectura) */}
      <CuestionarioBlock payload={c.submission_payload || {}} linkedin={c.linkedin_url} />

      {/* Decisión */}
      <div className="border border-white/10 rounded p-5 mb-4" data-testid="ficha-decision">
        <p className="text-white text-sm font-medium mb-4">Decisión</p>

        <div className="mb-5">
          <span className={lbl}>Resultado</span>
          <div className="flex flex-wrap gap-2">
            {DECISION_OPTIONS.map((o) => {
              const active = c.resultado_final === o.key;
              return (
                <button key={o.key} onClick={() => setField('resultado_final', active ? null : o.key)}
                  data-testid={`decision-${o.key.replace(/ /g, '-')}`}
                  className={`px-4 py-2 rounded-full border text-sm transition-all ${active ? o.active : o.idle}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <div className="md:col-span-1">
            <span className={lbl}>Evaluador</span>
            <select className={inp} value={c.evaluador || ''} onChange={(e) => setField('evaluador', e.target.value || null)}
              data-testid="ficha-evaluador">
              <option value="">—</option>
              {['Pablo', 'Roberto', 'Ambos'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <span className={lbl}>
              Nivel (R1/R2/R3)
              {c.nivel_manual_at && (
                <span className="ml-2 text-[#C58C40] text-[10px] normal-case" data-testid="ficha-nivel-modificado-badge">
                  · Modificado por admin
                </span>
              )}
            </span>
            <select className={inp} value={c.nivel_manual || c.submission_payload?.nivel || ''}
              onChange={(e) => setField('nivel_manual', e.target.value || null)}
              data-testid="ficha-nivel-manual">
              <option value="">—</option>
              {['R1', 'R2', 'R3'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {c.submission_payload?.nivel && (
              <p className="text-white/25 text-[10px] mt-1">Autodeclarado por el advisor: {c.submission_payload.nivel}</p>
            )}
          </div>
          <div className="md:col-span-1">
            <span className={lbl}>Notas internas (opcional)</span>
            <textarea className={inp} rows={3} value={c.notas_internas || ''}
              onChange={(e) => setField('notas_internas', e.target.value)}
              placeholder="Impresiones, contexto, motivo de la decisión…"
              data-testid="ficha-notas-internas" />
          </div>
        </div>

        <div className="mb-5">
          <span className={lbl}>
            Cobertura · verticales y roles
            <span className="ml-2 text-white/25 font-normal normal-case">(define en qué hueco de la matriz de cobertura cuenta este advisor)</span>
          </span>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(VMAP).map(([key, field]) => {
              const active = c[field] === 'Acreditada';
              return (
                <button key={key} type="button" onClick={() => setField(field, active ? 'No' : 'Acreditada')}
                  data-testid={`ficha-vertical-${key}`}
                  className={`px-3 py-1.5 rounded border text-xs transition-all ${
                    active ? 'bg-emerald-400/15 border-emerald-400/40 text-emerald-300' : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}>
                  {VLABEL[key]}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3">
            {ROLE_KEYS.map((rk, i) => (
              <div key={rk}>
                <span className="text-white/30 text-[10px] block mb-0.5 text-center">R{i + 1}</span>
                <select className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs outline-none focus:border-[#C58C40]/50"
                  value={c[rk] || 'No'} onChange={(e) => setField(rk, e.target.value)}
                  data-testid={`ficha-role-${rk}`}>
                  <option value="No">No</option>
                  <option value="Solido">Sólido</option>
                  <option value="Referencia">Referencia</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={saveDecision} disabled={saving} data-testid="ficha-save-decision"
            className="flex items-center gap-2 bg-[#C58C40] text-white rounded px-4 py-2 text-xs tracking-wide hover:bg-[#b07b35] transition-colors disabled:opacity-50">
            <Save size={13} />{saving ? 'Guardando…' : 'Guardar decisión'}
          </button>
          {saveMsg && (
            <span className={`text-xs ${saveMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
              {saveMsg.type === 'ok'
                ? <><Check size={12} className="inline mr-1" />{saveMsg.text}</>
                : <><AlertTriangle size={12} className="inline mr-1" />{saveMsg.text}</>}
            </span>
          )}
        </div>
      </div>

      {/* Correos */}
      <div className="border border-white/10 rounded p-5" data-testid="ficha-correos">
        <p className="text-white/60 text-sm font-medium mb-1 flex items-center gap-2">
          <Mail size={14} />Correos al advisor
        </p>
        <p className="text-white/30 text-[11px] mb-4">
          El correo se envía directamente al advisor con confirmación previa. Cada plantilla solo se envía una vez.
        </p>
        <div className="flex flex-wrap gap-2">
          {DECISION_OPTIONS.map((o) => {
            const sent = !!emailsSent[o.email];
            const isSending = sending === o.email;
            const disabled = sent || isSending || !c.email;
            return (
              <button key={o.email} onClick={() => sendEmail(o)} disabled={disabled}
                data-testid={`ficha-email-${o.email}`}
                className={`flex items-center gap-2 rounded px-3 py-2 text-xs border transition-colors ${
                  sent
                    ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-300 cursor-default'
                    : disabled
                      ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                      : 'bg-white/5 border-white/10 text-white/70 hover:text-white hover:border-[#C58C40]/40'
                }`}>
                {sent ? <Check size={13} /> : isSending ? <Send size={13} className="animate-pulse" /> : <Mail size={13} />}
                {o.emailLabel}
                {sent && <span className="text-emerald-400/70 text-[10px] ml-1">
                  · {(emailsSent[o.email] || '').slice(0, 10)}
                </span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded shadow-lg border text-sm max-w-sm ${
          toast.type === 'ok'
            ? 'bg-emerald-400/15 border-emerald-400/30 text-emerald-200'
            : 'bg-red-400/15 border-red-400/30 text-red-200'
        }`} data-testid="ficha-toast">
          {toast.type === 'ok' ? <Check size={14} className="inline mr-2" /> : <AlertTriangle size={14} className="inline mr-2" />}
          {toast.text}
        </div>
      )}
    </div>
  );
}
